import type { Product, DeliveryZone, CreateOrderPayload, Order, PaymentStatus } from "@cieda/types";
import { getMockData } from "./mock-storefront-data";

// In dev, use empty string so requests go through Vite's proxy (/api → localhost:3001).
// In production, VITE_API_URL must be set to the real API origin.
const API_URL = import.meta.env.VITE_API_URL || "";

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  try {
    const res = await fetch(`${API_URL}${path}`, {
      ...options,
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        ...options?.headers,
      },
    });

    if (!res.ok) {
      const error = await res.json().catch(() => ({ error: "Erreur réseau" }));
      throw new Error(error.error || `HTTP ${res.status}`);
    }

    return await res.json();
  } catch {
    const mock = getMockData(path);
    if (mock !== undefined) {
      console.info(`[MOCK] ${path} — using demo data`);
      return mock as T;
    }
    throw new Error("API indisponible");
  }
}

export const api = {
  catalog: {
    products: (params?: Record<string, string>) => {
      const qs = params ? "?" + new URLSearchParams(params).toString() : "";
      return request<{
        products: Product[];
        pagination: { page: number; limit: number; total: number };
      }>(`/api/catalog/products${qs}`);
    },
    product: (id: string) =>
      request<Product>(`/api/catalog/products/${id}`),
    deliveryZones: (regionId?: string) => {
      const qs = regionId ? `?region_id=${regionId}` : "";
      return request<DeliveryZone[]>(`/api/catalog/delivery-zones${qs}`);
    },
  },
  orders: {
    create: (payload: CreateOrderPayload) =>
      request<{
        order: { id: string; public_ref: string; status: string; total_ttc_fcfa: number; payment_method: string };
        payment_id: string | null;
        tracking_url: string;
      }>("/api/orders", {
        method: "POST",
        body: JSON.stringify(payload),
      }),
    track: (ref: string) =>
      request<{ order: Order; items: unknown[]; delivery: unknown }>(`/api/orders/track/${ref}`),
  },
  payments: {
    status: (id: string) =>
      request<{ status: PaymentStatus }>(`/api/payments/${id}/status`),
  },
  delivery: {
    zones: () =>
      request<{
        regions: Array<{
          id: string;
          name: string;
          code: string;
          active: boolean;
          zones: Array<{
            id: string;
            zone_name: string;
            fee_own_car_fcfa: number;
            fee_own_bike_fcfa: number | null;
            fee_yango_estimated_fcfa: number | null;
            estimated_time_minutes: number;
            centroid_lat: number | null;
            centroid_lng: number | null;
          }>;
        }>;
      }>("/api/delivery/zones"),
    config: () =>
      request<{
        delivery_mode: string;
        service_fee: { type: string; value: number };
        free_delivery_threshold_fcfa: number;
      }>("/api/delivery/config"),
    methods: () =>
      request<{
        methods: Array<{
          id: string;
          name: string;
          type: string;
          config: Record<string, unknown>;
        }>;
      }>("/api/delivery/methods"),
  },
  cms: {
    heroSlides: () =>
      request<{
        slides: Array<{
          id: string;
          title: string;
          title_highlight: string | null;
          badge: string | null;
          subtitle: string | null;
          image_url: string;
          cta_text: string | null;
          cta_link: string | null;
          sort_order: number;
        }>;
      }>("/api/cms/hero-slides"),
    brandShowcases: () =>
      request<{
        brands: Array<{
          id: string;
          brand_name: string;
          slug: string;
          tagline: string | null;
          description: string | null;
          logo_url: string | null;
          cover_image_url: string | null;
          accent_color: string;
          sort_order: number;
        }>;
      }>("/api/cms/brand-showcases"),
    activePromo: () =>
      request<{
        promo: {
          id: string;
          title: string;
          subtitle: string | null;
          cta_text: string | null;
          cta_link: string | null;
          bg_image_url: string | null;
        } | null;
      }>("/api/cms/active-promo"),
  },
};
